import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from './utils/currency';
import { useSortableTable } from './hooks/useSortableTable';
import { apiFetch } from './utils/api';
import PageHeader from './components/PageHeader';

function LabourCosts() {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  
  // Sorting
  const { items: sortedServices, requestSort, getSortIndicator } = useSortableTable(services, { key: 'name', direction: 'asc' });
  
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');

  // Column visibility
  const [visibleColumns, setVisibleColumns] = useState({
    productCode: true,
    productName: true,
    type: true,
    category: true,
    costPrice: true,
    price: true,
    markup: true,
    stock: true,
    location: true,
    barcode: false,
  });
  const [showColumnSelector, setShowColumnSelector] = useState(false);

  useEffect(() => {
    fetchServices();
    loadColumnPreferences();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await apiFetch('/api/products');
      const data = await response.json();
      // Filter to only Service type
      setServices(data.filter(p => p.type === 'Service'));
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadColumnPreferences = async () => {
    try {
      const response = await apiFetch('/api/preferences/default/labour-costs');
      const data = await response.json();
      if (data.preferences?.visibleColumns) {
        setVisibleColumns(data.preferences.visibleColumns);
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    }
  };

  const saveColumnPreferences = async (columns) => {
    try {
      await apiFetch('/api/preferences/default/labour-costs', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preferences: { visibleColumns: columns } })
      });
    } catch (error) {
      console.error('Error saving preferences:', error);
    }
  };

  const toggleColumn = (column) => {
    const updated = { ...visibleColumns, [column]: !visibleColumns[column] };
    setVisibleColumns(updated);
    saveColumnPreferences(updated);
  };

  const filteredServices = sortedServices.filter(product => {
    const matchesSearch = 
      product.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.product_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.barcode?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = filterType === 'all' || product.type === filterType;
    const matchesCategory = filterCategory === 'all' || product.product_category === filterCategory;
    
    return matchesSearch && matchesType && matchesCategory;
  });

  const categories = [...new Set(services.map(p => p.product_category).filter(Boolean))];

  if (loading) {
    return <div className="p-8 text-center">Loading labour costs...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <PageHeader 
        title="Labour Costs"
        subtitle={`${services.length} total`}
        backButton
        onBack={() => navigate('/stock')}
        backTitle="Back to Stock"
        sticky
        actionButton={
          <button
            onClick={() => navigate('/labour-costs/new')}
            className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm"
          >
            ➕ Add Labour Cost
          </button>
        }
      />

      <div className="px-4 py-3">
        {/* Filters */}
        <div className="mb-3 flex items-center gap-2">
          {/* Search */}
          <input
            type="text"
            placeholder="Search by name, code, or barcode..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />

          {/* Type Filter */}
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
          >
            <option value="all">All Types</option>
            <option value="Product">Product</option>
            <option value="Service">Service</option>
            <option value="Consumable">Consumable</option>
          </select>

          {/* Category Filter */}
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
          >
            <option value="all">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>

          {/* Column Selector */}
          <div className="relative">
            <button
              onClick={() => setShowColumnSelector(!showColumnSelector)}
              className="px-3 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg text-sm font-medium whitespace-nowrap"
            >
              ⚙️ Columns
            </button>
            
            {showColumnSelector && (
              <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                <div className="p-3 space-y-2">
                  {Object.keys(visibleColumns).map(column => (
                    <label key={column} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={visibleColumns[column]}
                        onChange={() => toggleColumn(column)}
                        className="rounded text-purple-600"
                      />
                      <span className="text-sm capitalize">
                        {column.replace(/([A-Z])/g, ' $1').trim()}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Services Table */}
        <div className="bg-white rounded border border-gray-200">
        <div className="overflow-auto max-h-[calc(100vh-240px)]">
          <table className="w-full">
            <thead className="bg-gray-300 sticky top-0 z-20 border-b border-gray-400">
              <tr>
                {visibleColumns.productCode && <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('product_code')}>Code{getSortIndicator('product_code')}</th>}
                {visibleColumns.productName && <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('name')}>Name{getSortIndicator('name')}</th>}
                {visibleColumns.type && <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('type')}>Type{getSortIndicator('type')}</th>}
                {visibleColumns.category && <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('category')}>Category{getSortIndicator('category')}</th>}
                {visibleColumns.costPrice && <th className="text-right px-3 py-2 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('cost_price')}>Cost{getSortIndicator('cost_price')}</th>}
                {visibleColumns.price && <th className="text-right px-3 py-2 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('price')}>Price{getSortIndicator('price')}</th>}
                {visibleColumns.markup && <th className="text-right px-3 py-2 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('markup_percent')}>Markup{getSortIndicator('markup_percent')}</th>}
                {visibleColumns.stock && <th className="text-right px-3 py-2 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('stock_level')}>Stock{getSortIndicator('stock_level')}</th>}
                {visibleColumns.location && <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('location')}>Location{getSortIndicator('location')}</th>}
                {visibleColumns.barcode && <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('barcode')}>Barcode{getSortIndicator('barcode')}</th>}
                <th className="text-right px-3 py-2 text-xs font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredServices.length === 0 ? (
                <tr>
                  <td colSpan="20" className="px-6 py-12 text-center text-gray-500">
                    {searchTerm || filterType !== 'all' || filterCategory !== 'all'
                      ? 'No labour costs match your filters'
                      : 'No labour costs yet. Click "Add Labour Cost" to get started.'}
                  </td>
                </tr>
              ) : (
                filteredServices.map((product, index) => (
                  <tr
                    key={product.id}
                    onClick={() => navigate(`/labour-costs/${product.id}`)}
                    className={`hover:bg-blue-200 cursor-pointer transition ${
                      index % 2 === 1 ? 'bg-blue-100' : 'bg-white'
                    }`}
                  >
                    {visibleColumns.productCode && (
                      <td className="px-3 py-2 text-sm font-medium text-gray-700">{product.product_code}</td>
                    )}
                    {visibleColumns.productName && (
                      <td className="px-3 py-2 text-sm text-gray-900">{product.product_name}</td>
                    )}
                    {visibleColumns.type && (
                      <td className="px-3 py-2 text-sm text-gray-700">
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          product.type === 'Product' ? 'bg-blue-100 text-blue-700' :
                          product.type === 'Service' ? 'bg-blue-100 text-purple-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {product.type}
                        </span>
                      </td>
                    )}
                    {visibleColumns.category && (
                      <td className="px-3 py-2 text-sm text-gray-700">{product.product_category || '-'}</td>
                    )}
                    {visibleColumns.costPrice && (
                      <td className="px-3 py-2 text-sm text-right text-gray-700">
                        {formatCurrency(product.cost_price)}
                      </td>
                    )}
                    {visibleColumns.price && (
                      <td className="px-3 py-2 text-sm text-right font-medium text-gray-900">
                        {formatCurrency(product.price)}
                      </td>
                    )}
                    {visibleColumns.markup && (
                      <td className="px-3 py-2 text-sm text-right text-gray-700">
                        {product.markup ? `${parseFloat(product.markup).toFixed(1)}%` : '-'}
                      </td>
                    )}
                    {visibleColumns.stock && (
                      <td className="px-3 py-2 text-sm text-right">
                        <span className={`font-medium ${
                          parseFloat(product.current_stock || 0) <= 0 ? 'text-red-600' :
                          parseFloat(product.current_stock || 0) < product.reorder_level ? 'text-orange-600' :
                          'text-green-600'
                        }`}>
                          {parseFloat(product.current_stock || 0).toFixed(0)}
                        </span>
                      </td>
                    )}
                    {visibleColumns.location && (
                      <td className="px-3 py-2 text-sm text-gray-700">{product.location || '-'}</td>
                    )}
                    {visibleColumns.barcode && (
                      <td className="px-3 py-2 text-sm text-gray-600">{product.barcode || '-'}</td>
                    )}
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/services/${product.id}`);
                        }}
                        className="text-purple-600 hover:text-purple-800 text-xs font-medium"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        </div>

        <div className="px-4 mt-2 text-xs text-gray-600">
          Showing {filteredServices.length} of {services.length}
        </div>
      </div>
    </div>
  );
}

export default LabourCosts;
